import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import PopoverMenu from "../ui/PopoverMenu";
import Button from "../ui/Button";
import { apiFetch, clearStoredToken, getStoredToken, getStoredUserEmail } from "../api/client";
import BrandMark from "../ui/BrandMark";
import AiAssistantChat from "../components/AiAssistantChat";

const AUTH_KEY = "mockAuth";
const USER_EMAIL_KEY = "estimation_station_user_email";
const DEV_EMAIL = "dev@estimationstation.local";

function isLoggedIn() {
  return Boolean(getStoredToken());
}

function navClass(isActive) {
  return [
    "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150",
    isActive
      ? "bg-red-700 text-white shadow-sm ring-1 ring-red-800/50"
      : "text-zinc-300 hover:bg-zinc-800 hover:text-white",
  ].join(" ");
}

function DotIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
      <circle cx="8" cy="8" r="5" fill="currentColor" />
    </svg>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = React.useState(isLoggedIn());
  const [canViewDevUsers, setCanViewDevUsers] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    const savedEmail = String(localStorage.getItem(USER_EMAIL_KEY) || "").trim().toLowerCase();
    const tokenEmail = String(getStoredUserEmail() || "").trim().toLowerCase();
    const effectiveEmail = tokenEmail || savedEmail;
    const localEmailIsDev = effectiveEmail === DEV_EMAIL;
    if (effectiveEmail && effectiveEmail !== savedEmail) {
      localStorage.setItem(USER_EMAIL_KEY, effectiveEmail);
    }
    setCanViewDevUsers(localEmailIsDev);
    if (!loggedIn) {
      return () => {
        mounted = false;
      };
    }
    (async () => {
      try {
        await apiFetch("/auth/dev/users", { method: "GET" });
        if (mounted) setCanViewDevUsers(true);
      } catch (_e) {
        if (mounted) setCanViewDevUsers(localEmailIsDev);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loggedIn]);

  const handleLogout = React.useCallback(() => {
    localStorage.setItem(AUTH_KEY, "false");
    localStorage.removeItem(USER_EMAIL_KEY);
    clearStoredToken();
    setLoggedIn(false);
    navigate("/");
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[linear-gradient(155deg,#0a0a0a_0%,#1f1f1f_35%,#c7c7c7_100%)]">
      <div className="mx-auto grid min-h-screen max-w-[1480px] grid-cols-1 gap-0 lg:grid-cols-[280px_1fr]">
        <aside className="border-b border-zinc-700/80 bg-zinc-950/95 px-5 py-6 backdrop-blur lg:border-b-0 lg:border-r">
          <div className="rounded-2xl border border-zinc-700 bg-zinc-900 px-4 py-4">
            <BrandMark size="sm" className="mb-3 [&_p:first-child]:text-white [&_p:last-child]:text-zinc-400" />
            <p className="mt-1 text-xs text-zinc-400">Build and review claims-ready estimates.</p>
          </div>
          <nav className="mt-6 grid gap-2">
            <NavLink to="/app/build" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Build Estimate from Photos</span>
            </NavLink>
            <NavLink to="/app/review" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Estimate Review</span>
            </NavLink>
            <NavLink to="/app/mitigation" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Mitigation Station</span>
            </NavLink>
            <NavLink to="/app/personal-property" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Contents Corner</span>
            </NavLink>
            <NavLink to="/app/jobs" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Jobs</span>
            </NavLink>
            <NavLink to="/app/crm" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>CRM</span>
            </NavLink>
            <NavLink to="/app/inspections" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Inspection Calendar</span>
            </NavLink>
            <NavLink to="/app/inspection-checklist" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Inspection Checklist</span>
            </NavLink>
            <NavLink to="/app/invoices" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Invoice Studio</span>
            </NavLink>
            <NavLink to="/app/guidelines" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Estimating Guidelines</span>
            </NavLink>
            <NavLink to="/app/insurance-companies" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Insurance Contacts</span>
            </NavLink>
            <NavLink to="/app/settings/billing" className={({ isActive }) => navClass(isActive)}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-700/70 text-zinc-100 transition group-hover:bg-zinc-600 group-[.active]:bg-white/10 group-[.active]:text-white">
                <DotIcon />
              </span>
              <span>Billing</span>
            </NavLink>
            {canViewDevUsers ? (
              <button
                type="button"
                onClick={() => navigate("/app/settings/dev-users")}
                className="mt-1 inline-flex items-center justify-center rounded-xl border border-red-500/60 bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-600"
              >
                Dev Users
              </button>
            ) : null}
          </nav>
        </aside>

        <section className="min-w-0">
          <header className="flex items-center justify-end border-b border-zinc-300/80 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
            {!loggedIn ? (
              <div className="flex items-center gap-2">
                <Link to="/pricing">
                  <Button variant="ghost">Pricing</Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary">Login / Sign up</Button>
                </Link>
              </div>
            ) : (
              <PopoverMenu
                trigger={
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-300 bg-white text-sm font-semibold text-zinc-700">
                    ES
                  </span>
                }
                items={[
                  {
                    key: "settings",
                    label: "Settings",
                    onClick: () => navigate("/app/settings/billing"),
                  },
                  { key: "divider", type: "divider" },
                  {
                    key: "logout",
                    label: "Logout",
                    onClick: handleLogout,
                  },
                ]}
              />
            )}
          </header>
          <main
            className="p-4 sm:p-6 lg:p-8"
            style={{
              backgroundImage:
                "linear-gradient(to bottom, rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Outlet />
          </main>
        </section>
      </div>
      <AiAssistantChat />
    </div>
  );
}
